/**
 * WHICH MONTH — the one control that decides what this screen reads.
 *
 * ★★ IT IS A CONTROL AND NOT A DEFAULT, AND THAT IS THE WHOLE ARGUMENT OF THIS FEATURE.
 * `getAuthorCalendar` requires both bounds (`authors-api.yaml`) and this app has no clock
 * (`features/cabinet/format.ts`, "THERE IS NO CLOCK IN THIS APP AND THERE MUST NOT BE"), so a
 * month has to be NAMED before anything can be read. `features/cabinet/format.ts` already gives
 * the rule for where a range may come from: "from the RESPONSE … or from an explicit choice, never
 * from the page". This is the explicit choice, and it lives in the address so it survives a reload
 * and can be sent to somebody.
 *
 * ★ `<input type="month">` RATHER THAN A HAND-BUILT PICKER. The browser's own control opens on the
 * device's today without this code ever reading a clock, its value is already `YYYY-MM`, and it is
 * keyboard-accessible and localised for free. `@mantine/dates` would be the richer answer and IS
 * NOT INSTALLED — `web/authors/package.json` lists `@mantine/core`, `@mantine/hooks`, `@moonx/ui`,
 * `react` and `react-dom` and nothing else — and adding a dependency is not this package's to do.
 * Reported in this package's notes rather than worked around.
 *
 * ★ THE ARROWS MOVE BY EXACTLY ONE MONTH and never by 30 days. `shiftMonth` normalises the year
 * rollover through `Date.UTC`, so December → January is a year change and not an invalid month.
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the prototype's row - a round ‹,
 * the month in bold, a round ›, then whatever the screen puts beside them (`children`: the Calendar's
 * "This month"), and the month field at the right. The field's old line "UTC months — every day on
 * this platform is a UTC day" is gone: the owner's plain-words rule keeps "UTC" off an author's
 * screen, and the months are still cut exactly as before.
 */
import type { ReactNode } from 'react';
import { ActionIcon, Input } from '@mantine/core';
import { ChevronLeft, ChevronRight } from '@moonx/ui';
import { useTranslation } from '../../../i18n';
import { monthLabel, shiftMonth } from '../domain/month';
import type { MonthKey } from '../domain/month';
import classes from './calendar.module.css';

export interface MonthChooserProps {
  readonly month: MonthKey;
  readonly onChange: (month: MonthKey) => void;
  readonly busy?: boolean;
  readonly locale?: string;
  /** Drawn after the › arrow - the Calendar's "This month". */
  readonly children?: ReactNode;
}

export function MonthChooser({
  month,
  onChange,
  busy = false,
  locale,
  children,
}: MonthChooserProps) {
  const { t } = useTranslation('calendar');
  return (
    <div className={classes.chooser}>
      <div className={classes.chooserNav}>
        <ActionIcon
          variant="default"
          radius="xl"
          size="lg"
          aria-label={t('chooser.show', { month: monthLabel(shiftMonth(month, -1), locale) })}
          disabled={busy}
          onClick={() => {
            onChange(shiftMonth(month, -1));
          }}
        >
          {/* A glyph, with the button's own aria-label carrying the meaning. An arrow alone
              announces as nothing useful, which is not a month. */}
          <ChevronLeft size={15} aria-hidden />
        </ActionIcon>

        <span className={classes.monthName}>{monthLabel(month, locale)}</span>

        <ActionIcon
          variant="default"
          radius="xl"
          size="lg"
          aria-label={t('chooser.show', { month: monthLabel(shiftMonth(month, 1), locale) })}
          disabled={busy}
          onClick={() => {
            onChange(shiftMonth(month, 1));
          }}
        >
          <ChevronRight size={15} aria-hidden />
        </ActionIcon>

        {children}
      </div>

      <Input
        component="input"
        type="month"
        aria-label={t('chooser.goTo')}
        size="sm"
        radius="xl"
        value={month}
        disabled={busy}
        onChange={(event) => {
          // An empty value is what the control reports when it is cleared. Ignored rather than
          // propagated: there is no "no month" state to fall back to once one has been chosen,
          // and clearing the address would re-run the opening derivation for no reason.
          const next = (event.target as HTMLInputElement).value;
          if (next !== '') {
            onChange(next);
          }
        }}
      />
    </div>
  );
}
